import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import {
  IDepartments,
  IProduct,
  IVariant,
  TProductVendor,
  ICategories,
  ISubcategories,
} from "redux/reducers/models/product";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import Page from "components/page/Page";
import FormLayout from "components/form/FormLayout";
import { Select } from "components/select/index";
import Card from "components/card/Card";
import { IPrimaryPageAction } from "components/page/PageActions";
import "./newReceiving.scss";
import Portal from "elements/Portal";
import Popup from "components/popup/Popup";
import ReactDOM from "react-dom";
import DataTable from "../../../../../components/dataTable/DataTable";
import Input from "components/form/input";
import { ButtonNew as Button } from "components/buttonNew";
import Sheet from "components/sheet/Sheet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark, faSquareInfo } from "@fortawesome/pro-solid-svg-icons";
import { faTimes, faTrashCanXmark } from "@fortawesome/pro-regular-svg-icons";
import axios, { CancelToken } from "axios";
import { StatusCode } from "api/protocols";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { cloneDeep } from "lodash";
import { PostInventoryRecords } from "api/rpc/2024-04/facilityAdmin/product/inventory";
import Icon from "components/icon/Icon";
import { GetVendor } from "api/rpc/2024-04/facilityAdmin/product/vendor";
import { TSku } from "../inventoryCounts/NewInventoryCount";
import { findSkuProduct } from "../inventoryCounts/inventoryHelpers";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Spin from "components/spin/spin";
import { LocaleCurrency } from "helpers/Locale";
import { displayCurrency, errorMessage } from "helpers/Helpers";
import { GetDepartments, GetDepartmentDefaults } from "api/rpc/2024-04/facilityAdmin/facility/department";
import SelectNew from "components/select/SelectNew";
import { GetProduct, PostProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { IPurchaseOrder } from "redux/reducers/models/purchaseOrder";
import { GetPurchaseOrder, PutQuantityReceivedPurchaseOrderItems } from "api/rpc/2024-04/facilityAdmin/order/purchaseOrder";
import { PostCreditMemo, PostCreditMemoItems } from "api/rpc/2024-04/facilityAdmin/order/creditMemo";
import AddFolderProductsModal from "components/folders/AddFolderProductsModal";
import { defaultBrowseFilter, IBrowseFilter } from "components/folders/productBrowseFilters";
import { appendUniqueProducts, buildSelectAllProductBatch, fetchFolderProductSearchPage, IFolderProductSearchContext } from "components/folders/folderProductSearch";
// import moment from "moment";

interface IProductState {
  productsToAdd: Array<IProduct>;
  selectedProducts: Array<IProduct>;
  finalSelectedProducts: Array<IProduct>;
  skusNotFound: Array<TSku>;
  openModal: boolean;

  productQuantities: Record<number, number>;
  addedVariants: Array<Partial<IVariant>>;
  productSearchResults: IProduct[] | undefined;
  productSearchTotalCount: number | undefined;
  productSearchPending: boolean;
  productSearchLoadingMore: boolean;
  productSearchSelectAllPending: boolean;
}

interface ISkuState {
  skus: Array<TSku>;
  skuString: string;
}

/**Extra details on the receving
 * Ex: Vendor and Packing Slip
 */
interface IDetailState {
  packingSlip: string;
  po_number: string;
  vendor: TProductVendor;
  vendorQuery: string;
  searching: boolean;
  vendors: Array<TProductVendor>;
  purchaseOrder: IPurchaseOrder;
  purchaseOrders: Array<IPurchaseOrder>;
  purchaseOrderQuery: string;
}

enum TransferPage {
  InventoryDetails = 0,
  InventorySelection = 1,
  InventoryConfirmation = 2,
}

interface IVariantNotFoundState {
  modalOpen: boolean;
  sku: string;
  productName: string;
  productPrice: number;
  productDepartmentId: number | null;
  productCategoryId: number | null;
  productSubcategoryId: number | null;
  noDefaultsWarning: boolean;
}

type TBrowseFilterState = {
  search: string;
  browseFilter: IBrowseFilter;
};

type TBrowseDepartment = IDepartments & { cateogries?: ICategories[] };

const ITEMS_SELECT_ALL_LIMIT = 200;

function receivingStamp() {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
}

type TPurchaseOrderDiscrepancy = {
  variant_id: number;
  product_title: string;
  variant_title: string;
  current_quantity: number;
  expected_quantity: number;
  ordered_quantity: number;
  unit_cost: number;
};

export default function NewReceiving() {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { facilityStore } = useAppSelector(store => store);
  const { Option } = Select;
  const [pageTitle, setPageTitle] = useState<{ title: string; subtitle: string }>({ title: "", subtitle: "" });
  const [departments, setDepartments] = useState<IDepartments[]>([]);

  const [productState, setProductState] = useState<IProductState>({
    productsToAdd: [],
    selectedProducts: [],
    finalSelectedProducts: [],
    skusNotFound: [],
    openModal: false,

    productQuantities: {},
    addedVariants: [],
    productSearchResults: undefined,
    productSearchTotalCount: undefined,
    productSearchPending: false,
    productSearchLoadingMore: false,
    productSearchSelectAllPending: false,
  });

  const [totals, setTotals] = useState<{ quantity: number; cost: number }>({
    quantity: 0,
    cost: 0,
  });

  const [skuState, setSkuState] = useState<ISkuState>({
    skus: [],
    skuString: "",
  });

  const [detailState, setDetailState] = useState<IDetailState>({
    packingSlip: "",
    po_number: "",
    vendor: null,
    vendorQuery: "",
    searching: false,
    vendors: [],
    purchaseOrder: null,
    purchaseOrders: [],
    purchaseOrderQuery: "",
  });

  const [variantNotFoundState, setVariantNotFoundState] = useState<IVariantNotFoundState>({
    modalOpen: false,
    sku: "",
    productName: "",
    productPrice: 0,
    productDepartmentId: null,
    productCategoryId: null,
    productSubcategoryId: null,
    noDefaultsWarning: false,
  });

  const [page, setPage] = useState<TransferPage>(TransferPage.InventoryDetails);

  const [submitPopupState, setSubmitPopupState] = useState<boolean>(false);

  const [browseFilterState, setBrowseFilterState] = useState<TBrowseFilterState>({
    search: "",
    browseFilter: defaultBrowseFilter,
  });

  const [browseDepartments, setBrowseDepartments] = useState<TBrowseDepartment[]>([]);
  const [browseMetadataLoading, setBrowseMetadataLoading] = useState(false);
  const [uncategorizedCount, setUncategorizedCount] = useState(0);
  const browseFilterRef = useRef(browseFilterState.browseFilter);
  const prevAddProductsModalOpen = useRef(false);

  const excludedVariantIds = useMemo(
    () => productState.addedVariants.map(variant => variant.id).filter((id): id is number => id != null),
    [productState.addedVariants],
  );
  const productSearchCancelTokenRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(null);
  const excludedVariantIdSet = useMemo(() => new Set(excludedVariantIds), [excludedVariantIds]);

  const purchaseOrderItemExists = useMemo(() => {
    let purchaseOrderItemExists = false;
    if (detailState?.purchaseOrder && detailState?.purchaseOrder?.items) {
      productState?.finalSelectedProducts?.forEach(product => {
        product?.variants?.forEach(variant => {
          if (detailState?.purchaseOrder?.items?.some(item => item?.variant_id === variant?.id)) {
            purchaseOrderItemExists = true;
          }
        });
      });
    }
    return purchaseOrderItemExists;
  }, [productState?.finalSelectedProducts, detailState?.purchaseOrder?.items]);

  const purchaseOrderItemDiscrepencies = useMemo(() => {
    const discrepancies: Array<TPurchaseOrderDiscrepancy> = [];
    const items = detailState?.purchaseOrder?.items;
    if (!items?.length) {
      return discrepancies;
    }

    const receivedByVariant = new Map<number, { quantity: number; product_title: string; variant_title: string }>();
    productState?.finalSelectedProducts?.forEach(product => {
      product?.variants?.forEach(variant => {
        if (variant?.id == null) {
          return;
        }
        receivedByVariant.set(variant.id, {
          quantity: Number(variant.quantity ?? 0),
          product_title: product?.preferred_title ? product.preferred_title : product.title,
          variant_title: variant.title,
        });
      });
    });

    items.forEach(item => {
      if (item?.variant_id == null) {
        return;
      }
      const orderedQuantity = Number(item.quantity);
      const openQuantity = orderedQuantity - Number(item.quantity_received ?? 0);
      const received = receivedByVariant.get(item.variant_id);
      const currentQuantity = received?.quantity ?? 0;
      if (currentQuantity === openQuantity) {
        return;
      }
      discrepancies.push({
        variant_id: item.variant_id,
        product_title: received?.product_title ?? item.product_name,
        variant_title: received?.variant_title ?? item.variant_name,
        current_quantity: currentQuantity,
        expected_quantity: openQuantity,
        ordered_quantity: orderedQuantity,
        unit_cost: Number(item.unit_cost ?? 0),
      });
    });

    return discrepancies;
  }, [productState?.finalSelectedProducts, detailState?.purchaseOrder?.items]);

  const purchaseOrderShortfalls = useMemo(
    () => purchaseOrderItemDiscrepencies.filter(item => item.expected_quantity - item.current_quantity > 0),
    [purchaseOrderItemDiscrepencies],
  );

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (page === TransferPage.InventorySelection) {
      setProductState(prevState => ({ ...prevState, finalSelectedProducts: [], skusNotFound: [] }));
      if (detailState?.purchaseOrder && productState?.selectedProducts?.length === 0) {
        void loadPurchaseOrderWithItems(detailState?.purchaseOrder?.id, source.token);
      }
    } else if (page === TransferPage.InventoryConfirmation) {
      setProductState(prevState => ({
        ...prevState,
        finalSelectedProducts: cloneDeep(
          prevState.selectedProducts?.length ? prevState.selectedProducts : productState.selectedProducts,
        ),
        skusNotFound: cloneDeep(skuState.skus),
      }));
    }
    const pageTitle = generatePageTitle();
    setPageTitle(pageTitle);
    return () => {
      source.cancel();
    };
  }, [page]);

  useEffect(() => {
    const purchaseOrderId = Number(new URLSearchParams(location.search).get("purchase_order_id"));
    if (!purchaseOrderId) {
      return;
    }
    const source = axios.CancelToken.source();
    void loadPurchaseOrderWithItems(purchaseOrderId, source.token, true);
    return () => source.cancel();
  }, []);

  //Search variants by sku / barcode
  useEffect(() => {
    const source = axios.CancelToken.source();
    const currentSku = skuState?.skuString?.slice();
    const skuIndex = skuState?.skus?.findIndex(sku => sku.sku === currentSku);
    setSkuState(prevState => ({ ...prevState, skuString: "" }));
    if (skuIndex !== -1) {
      const foundSku = skuState.skus[skuIndex];
      // First sku added, search to see if variant exists. Or, sku search hasn't completed yet
      if (foundSku.quantity === 1 || !foundSku?.searched) {
        void findSkuProduct(
          productState.selectedProducts,
          skuState.skus,
          setProductState,
          setSkuState,
          dispatch,
          source.token,
        );
      }
    }
    return () => {
      source.cancel();
    };
  }, [skuState?.skus]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadDepartments(source.token);
    return () => {
      source.cancel();
    };
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();

    let timeoutId: NodeJS.Timeout = null;
    timeoutId = global.setTimeout(() => {
      void loadVendors(source.token);
    }, 300);

    return () => {
      source.cancel();
      clearTimeout(timeoutId);
    };
  }, [detailState.vendorQuery]);

  useEffect(() => {
    const source = axios.CancelToken.source();

    let timeoutId: NodeJS.Timeout = null;
    timeoutId = global.setTimeout(() => {
      void loadPurchaseOrders(source.token);
    }, 300);

    return () => {
      source.cancel();
      clearTimeout(timeoutId);
    };
  }, [detailState.purchaseOrderQuery]);

  useEffect(() => {
    if (productState?.selectedProducts) {
      let updatedQuantity = 0;
      let updatedCost = 0;
      if (page === TransferPage.InventorySelection) {
        updatedQuantity = productState?.selectedProducts?.reduce((totalQuantity, product) => {
          return (
            totalQuantity +
            product?.variants?.map(variant => Number(variant?.quantity))?.reduce((total, amount) => total + amount, 0)
          );
        }, 0);
        updatedCost = productState?.selectedProducts?.reduce((totalCost, product) => {
          return (
            totalCost +
            product?.variants
              ?.map(variant => variant?.cost * Number(variant?.quantity))
              ?.reduce((total, amount) => total + amount, 0)
          );
        }, 0);
      } else {
        updatedQuantity = productState?.finalSelectedProducts?.reduce((totalQuantity, product) => {
          return (
            totalQuantity +
            product?.variants?.map(variant => Number(variant?.quantity))?.reduce((total, amount) => total + amount, 0)
          );
        }, 0);
        updatedCost = productState?.finalSelectedProducts?.reduce((totalCost, product) => {
          return (
            totalCost +
            product?.variants
              ?.map(variant => variant?.cost * Number(variant?.quantity))
              ?.reduce((total, amount) => total + amount, 0)
          );
        }, 0);
      }

      setTotals({
        quantity: updatedQuantity,
        cost: updatedCost,
      });
    }
  }, [productState?.selectedProducts, productState?.finalSelectedProducts]);

  useEffect(() => {
    if (!productState.openModal) {
      productSearchCancelTokenRef.current?.cancel();
      setProductState(prevState => ({
        ...prevState,
        productSearchPending: false,
        productSearchLoadingMore: false,
      }));
      prevAddProductsModalOpen.current = false;
      return;
    }

    const justOpened = !prevAddProductsModalOpen.current;
    prevAddProductsModalOpen.current = true;

    const browseChanged =
      browseFilterRef.current.type !== browseFilterState.browseFilter.type ||
      browseFilterRef.current.id !== browseFilterState.browseFilter.id;
    browseFilterRef.current = browseFilterState.browseFilter;
    const delay = browseChanged ? 0 : 1000;

    let mounted = true;
    productSearchCancelTokenRef.current?.cancel();
    const cancelSource = axios.CancelToken.source();
    productSearchCancelTokenRef.current = cancelSource;

    setProductState(prevState => {
      const hasLoadedResults = (prevState.productSearchResults?.length ?? 0) > 0;

      return {
        ...prevState,
        productSearchPending: true,
        productSearchLoadingMore: false,
        ...(hasLoadedResults ? {} : { productSearchResults: undefined, productSearchTotalCount: undefined }),
      };
    });

    const runFetch = async () => {
      try {
        const { products, totalCount } = await fetchFolderProductSearchPage(
          GetProduct,
          getProductSearchContext(),
          0,
          true,
          cancelSource.token,
          () => dispatch(showError(t("Failed to get products"))),
        );
        if (mounted) {
          setProductState(prevState => ({
            ...prevState,
            productSearchResults: products,
            productSearchTotalCount: totalCount,
            productSearchPending: false,
            productSearchLoadingMore: false,
          }));
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          return;
        }
        if (mounted) {
          setProductState(prevState => ({
            ...prevState,
            productSearchResults: [],
            productSearchTotalCount: 0,
            productSearchPending: false,
            productSearchLoadingMore: false,
          }));
        }
      }
    };

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (justOpened) {
      void runFetch();
    } else {
      timeoutId = global.setTimeout(() => {
        void runFetch();
      }, delay);
    }

    return () => {
      mounted = false;
      cancelSource.cancel();
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [browseFilterState.search, browseFilterState.browseFilter, productState?.openModal]);

  function generatePageTitle() {
    switch (page) {
      case TransferPage.InventoryDetails:
        return {
          title: t("Inventory Details"),
          subtitle: t("Step 1 of 3"),
        };
      case TransferPage.InventorySelection:
        return {
          title: t("Select Inventory"),
          subtitle: t("Step 2 of 3"),
        };
      case TransferPage.InventoryConfirmation:
        return {
          title: t("Confirm Selection"),
          subtitle: t("Step 3 of 3"),
        };
      default:
        return { title: t("New Inventory Receiving"), subtitle: "" };
    }
  }

  async function loadVendors(token?: CancelToken) {
    //Show loader
    setDetailState(prev => ({ ...prev, vendors: [], searching: true }));

    const res = await GetVendor({ search: detailState.vendorQuery ?? undefined }, token ? false : true, token);
    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting vendors")));
    }
    setDetailState(prev => ({ ...prev, vendors: res.status !== StatusCode.OK ? [] : res.data, searching: false }));
  }

  async function loadPurchaseOrders(token?: CancelToken) {
    //Show loader
    setDetailState(prev => ({ ...prev, purchaseOrders: [], searching: true }));

    const res = await GetPurchaseOrder({ search: detailState.purchaseOrderQuery ?? undefined, statuses: ["ordered", "partially_received"] }, token ? false : true, token);
    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting purchase orders")));
    }
    setDetailState(prev => ({ ...prev, purchaseOrders: res.status !== StatusCode.OK ? [] : res.data, searching: false }));
  }

  async function loadPurchaseOrderWithItems(id: number, token?: CancelToken, openConfirmation = false) {
    dispatch(enqueue());
    const res = await GetPurchaseOrder({ id, statuses: ["ordered", "partially_received"], extended: true }, false, token);
    if (token && token.reason) {
      dispatch(dequeue());
      return;
    }
    if (res.status !== StatusCode.OK || res?.data?.length <= 0) {
      dispatch(dequeue());
      dispatch(showError(t("Error getting purchase order")));
      return;
    }

    let purchaseOrder = res?.data[0];
    purchaseOrder = { ...purchaseOrder, items: purchaseOrder?.items?.filter(item => (item?.quantity_received ?? 0) < item?.quantity) };

    const productIds = purchaseOrder?.items?.map(item => item?.product_id);
    const productIdSet = new Set(productIds);

    if (!productIds?.length) {
      dispatch(dequeue());
      dispatch(showError(t("This purchase order has no items left to receive")));
      return;
    }

    const productRes = await GetProduct({ ids: Array.from(productIdSet), variants: true, all: true }, false);
    if (token && token.reason) {
      dispatch(dequeue());
      return;
    }
    if (productRes.status !== StatusCode.OK || productRes?.data?.length <= 0) {
      dispatch(dequeue());
      dispatch(showError(t("Error getting purchase order products")));
      return;
    }
    dispatch(dequeue());
    const products = productRes?.data;

    const finalProducts = products?.map(product => ({
      ...product, variants: product?.variants?.filter(variant => purchaseOrder?.items?.some(item => item?.variant_id === variant?.id))?.map(variant => {
        const foundItem = purchaseOrder?.items?.find(item => item?.variant_id === variant?.id);
        const remaining = Math.max(Number(foundItem?.quantity ?? 0) - Number(foundItem?.quantity_received ?? 0), 0);
        return { ...variant, quantity: remaining, cost: foundItem?.unit_cost ?? variant?.cost };
      }),
    }));

    ReactDOM.unstable_batchedUpdates(() => {
      setDetailState(prev => ({
        ...prev,
        purchaseOrder,
        po_number: purchaseOrder?.po_number ?? "",
        vendor: purchaseOrder?.vendor_id
          ? { id: purchaseOrder.vendor_id, title: purchaseOrder.vendor_name } as TProductVendor
          : prev.vendor,
        searching: false,
      }));
      setProductState(prev => ({
        ...prev,
        selectedProducts: finalProducts,
        finalSelectedProducts: openConfirmation ? finalProducts : prev.finalSelectedProducts,
      }));
      if (openConfirmation) {
        setPage(TransferPage.InventoryConfirmation);
      }
    });
  }

  function handleEnterKeydown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.code === "Enter" || e.code === "NumpadEnter") {
      void addSku();
    }
  }

  async function createShortfallCreditMemo() {
    const purchaseOrder = detailState?.purchaseOrder;
    const facility = facilityStore?.facility;
    const shortfalls = purchaseOrderShortfalls.filter(item => item.variant_id && item.expected_quantity - item.current_quantity > 0);
    if (!purchaseOrder || !facility || shortfalls.length === 0) {
      return null;
    }

    const creditMemoRes = await PostCreditMemo({
      vendor_id: purchaseOrder.vendor_id ?? null,
      purchase_order_id: purchaseOrder.id,
      status: "draft",
      source: "receiving_shortfall",
      credit_memo_number: `CM-${purchaseOrder.po_number}-${receivingStamp()}`,
      facility_name: facility.long_name,
      facility_address_line: facility.address_line_1,
      facility_city: facility.city,
      facility_province: facility.province_name,
      facility_postal: facility.postal,
      facility_country: facility.country_name,
      vendor_name: purchaseOrder.vendor_name,
      vendor_address_line: purchaseOrder.vendor_address_line,
      vendor_city: purchaseOrder.vendor_city,
      vendor_province: purchaseOrder.vendor_province,
      vendor_postal: purchaseOrder.vendor_postal,
      vendor_country: purchaseOrder.vendor_country,
      vendor_contact_name: purchaseOrder.vendor_contact_name,
      vendor_email: purchaseOrder.vendor_email,
      vendor_phone: purchaseOrder.vendor_phone,
      notes: t("Created from purchase order {{poNumber}} receiving. Quantities are the amount short of the purchase order.", { poNumber: purchaseOrder.po_number }),
    }, true);

    if (creditMemoRes.status !== StatusCode.OK) {
      dispatch(showError(errorMessage(creditMemoRes, t("Receiving was saved, but the credit memo could not be created"))));
      return null;
    }

    const itemRes = await PostCreditMemoItems({
      credit_memo_id: creditMemoRes.data.id,
      variants: shortfalls.map(item => ({
        variant_id: item.variant_id,
        quantity: item.expected_quantity - item.current_quantity,
        unit_cost: item.unit_cost,
      })),
    }, true);

    if (itemRes.status !== StatusCode.OK) {
      dispatch(showError(errorMessage(itemRes, t("Receiving was saved, but the credit memo items could not be added"))));
      return creditMemoRes.data.id;
    }

    return creditMemoRes.data.id;
  }

  async function handleCreateNewReceiving(createCreditMemoForShortfalls = false) {
    const variants: Array<IVariant> = [];
    productState?.finalSelectedProducts?.forEach(product => {
      variants?.push(...[...product.variants]);
    });
    const formattedVariants = variants
      ?.filter(variant => Number(variant?.quantity) > 0)
      .map(variant => ({
        variant_id: variant?.id,
        quantity_receiving: variant?.quantity,
        cost: variant?.cost,
      }));

    if (!formattedVariants?.length) {
      dispatch(showError(t("Enter a quantity for at least one item")));
      return;
    }

    const res = await PostInventoryRecords(
      {
        variants: formattedVariants,
        vendor_id: detailState?.vendor?.id ?? undefined,
        packing_slip_number: detailState?.packingSlip ?? undefined,
        po_number: detailState?.po_number ?? undefined,
        purchase_order_id: detailState?.purchaseOrder ? detailState?.purchaseOrder?.id : undefined,
      },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating new receiving")));
      return;
    }

    // Update quantity received on the items of the purchase order. Unreceived quantities stay open.
    if (detailState?.purchaseOrder) {
      const purchaseOrderUpdated = await updatePOQuantityReceived();
      if (!purchaseOrderUpdated) {
        return;
      }
    }

    dispatch(showSuccess(t("Successfully added receivings")));
    setSubmitPopupState(false);

    if (createCreditMemoForShortfalls && detailState?.purchaseOrder && purchaseOrderShortfalls.length > 0) {
      const creditMemoId = await createShortfallCreditMemo();
      if (creditMemoId) {
        dispatch(showSuccess(t("Credit memo created for the shorted items")));
        history.push(`/admin/settings/credit-memos/${creditMemoId}`);
        return;
      }
    }

    history.push("/admin/settings/inventory");
  }

  async function updatePOQuantityReceived() {
    const items: Array<{ id: number; quantity_received: number }> = [];
    if (detailState?.purchaseOrder && detailState?.purchaseOrder?.items) {
      productState?.finalSelectedProducts?.forEach(product => {
        product?.variants?.forEach(variant => {
          const receivedQuantity = Number(variant?.quantity);
          if (!(receivedQuantity > 0)) {
            return;
          }
          const foundVariantItem = detailState?.purchaseOrder?.items?.find(item => item?.variant_id === variant?.id);
          if (foundVariantItem) {
            items?.push({ id: foundVariantItem?.id, quantity_received: receivedQuantity });
          }
        });
      });
    }
    if (items?.length === 0) {
      return true;
    }
    const res = await PutQuantityReceivedPurchaseOrderItems({ purchase_order_id: detailState?.purchaseOrder?.id, items: items }, true);
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(errorMessage(res, t("Error updating purchase order item quantities"))));
      return false;
    }
    return true;
  }

  function addSku() {
    if (skuState.skuString) {
      const updatedSkus = [...skuState.skus];
      const productIncremented = incrementProductQuantity(skuState.skuString);
      if (!productIncremented) {
        const foundSkuIndex = updatedSkus?.findIndex(skuObj => skuObj.sku === skuState.skuString);
        if (foundSkuIndex !== -1) {
          //Increment quantity of sku found
          const foundSku = updatedSkus[foundSkuIndex];
          updatedSkus.splice(foundSkuIndex, 1, { ...foundSku, quantity: ++foundSku.quantity });
        } else {
          //Add new sku
          const newSku: TSku = { sku: skuState.skuString, quantity: 1, searched: false };
          updatedSkus.push(newSku);
        }
        setSkuState(prevState => ({ ...prevState, skus: updatedSkus }));
      } else {
        setSkuState(prevState => ({ ...prevState, skuString: "" }));
      }
    }
  }

  function incrementProductQuantity(currentSku: string) {
    const products = [...productState.selectedProducts];
    let productInList = false;
    let variantIndex: number = null;
    const productIndex = products?.findIndex(product => {
      const foundVariantIndex = product?.variants?.findIndex(
        variant => variant?.sku === currentSku || variant?.barcode === currentSku,
      );
      if (foundVariantIndex !== -1) {
        variantIndex = foundVariantIndex;
        return true;
      }
    });
    if (productIndex !== -1 && variantIndex !== -1) {
      // Product already in list, increment the quantity
      const foundVariant = products[productIndex]?.variants[variantIndex];
      products[productIndex].variants[variantIndex] = { ...foundVariant, quantity: ++foundVariant.quantity };
      productInList = true;
      setProductState(prevState => ({ ...prevState, selectedProducts: products }));
    }
    return productInList;
  }

  function handleProductSelect(products: Array<IProduct>) {
    const selectedProducts = applyProductQuantities(products, productState.productQuantities);
    const { updatedSkus, updatedProducts } = updateSkus(selectedProducts);
    ReactDOM.unstable_batchedUpdates(() => {
      setProductState(prevState => ({ ...prevState, selectedProducts: updatedProducts, openModal: false, finalSelectedProducts: page === TransferPage.InventoryConfirmation ? updatedProducts : prevState?.finalSelectedProducts}));
      setSkuState(prevState => ({ ...prevState, skus: updatedSkus }));
      setBrowseFilterState({ search: "", browseFilter: defaultBrowseFilter });
    });
  }

  function handleEditProduct(productId: number) {
    window.open("/admin/product/" + String(productId));
  }

  function updateSkus(products: Array<IProduct>) {
    const updatedSkus = [...skuState.skus];
    const updatedProducts = [...products];
    updatedProducts?.forEach((product, productIndex) => {
      product?.variants?.forEach((variant, variantIndex) => {
        const skuIndex = updatedSkus?.findIndex(sku => sku.sku === variant?.sku || sku.sku === variant?.barcode);
        if (skuIndex !== -1) {
          updatedProducts[productIndex].variants[variantIndex] = {
            ...variant,
            quantity: Number(variant.quantity) + Number(updatedSkus[skuIndex]?.quantity),
          };
          updatedSkus.splice(skuIndex, 1);
        }
      });
    });
    return { updatedSkus: updatedSkus, updatedProducts: updatedProducts };
  }

  function handleEnterClick() {
    const skuSearchId = document.getElementById("skuSearchId");
    skuSearchId.focus();
    void addSku();
  }

  function removeSku(index: number) {
    const updatedSkus = [...skuState.skus];
    // Sku cannot be removed if search is in progress
    const allSkusSearched = updatedSkus?.every(sku => sku.searched);
    if (updatedSkus[index] && allSkusSearched) {
      updatedSkus.splice(index, 1);
      setSkuState(prevState => ({ ...prevState, skus: updatedSkus }));
    }
  }

  function removeVariant(
    productIndex: number,
    key: keyof Pick<IProductState, "finalSelectedProducts" | "selectedProducts">,
    variantIndex?: number,
  ) {
    const updatedProducts = [...productState[key]];
    //Product with single variant
    if (variantIndex == null || updatedProducts[productIndex]?.variants?.length === 1) {
      updatedProducts.splice(productIndex, 1);
    } else {
      // Product with multiple variants
      if (updatedProducts[productIndex]?.variants[variantIndex]) {
        updatedProducts[productIndex]?.variants?.splice(variantIndex, 1);
      }
    }
    setProductState(prevState => ({ ...prevState, [key]: updatedProducts }));
  }

  function handleInputChange(
    e: React.ChangeEvent<HTMLInputElement>,
    key: keyof Pick<IProductState, "finalSelectedProducts" | "selectedProducts">,
    productIndex: number,
    variantIndex: number,
    field: string = "quantity",
  ) {
    const { valueAsNumber } = e.target;
    const updatedProducts = [...productState[key]];
    const updatedVariant = updatedProducts[productIndex].variants[variantIndex];
    updatedProducts[productIndex].variants[variantIndex] = {
      ...updatedVariant,
      [field]: isNaN(valueAsNumber) ? null : valueAsNumber,
    };
    setProductState(prevState => ({ ...prevState, [key]: updatedProducts }));
  }

  async function loadDepartments(token?: CancelToken) {
    const res = await GetDepartments({ extended: true }, token ? false : true, token);
    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      return;
    }
    setDepartments(res.data);
  }

  const disablePrimaryAction =
    (!detailState?.purchaseOrder && (page === TransferPage.InventorySelection &&
      (productState?.selectedProducts?.length === 0 ||
        skuState?.skus?.some(sku => !sku.searched) ||
        productState?.selectedProducts?.some(product => product?.variants?.some(variant => !variant.quantity))))) ||
    (page === TransferPage.InventoryConfirmation &&
      (productState.finalSelectedProducts?.length === 0 ||
        productState?.finalSelectedProducts?.some(product => product?.variants?.some(variant => !variant.quantity))));

  const continueAction: IPrimaryPageAction = {
    content: t("Continue"),
    action: () => setPage(prevTab => ++prevTab),
    disabled: disablePrimaryAction,
  };

  const hasPositiveReceivingQuantity = productState.finalSelectedProducts?.some(product =>
    product?.variants?.some(variant => Number(variant?.quantity) > 0),
  );

  const submitAction: IPrimaryPageAction = {
    content: t("Submit"),
    action: () => setSubmitPopupState(true),
    disabled: Boolean(detailState?.purchaseOrder) && !hasPositiveReceivingQuantity,
  };

  async function handleCreateNewProduct() {
    // Must have at least a department selected
    if (!variantNotFoundState.productDepartmentId) {
      dispatch(showError(t("Please select at least a department before creating a product.")));
      return;
    }

    const departmentIdsToCheck: number[] = [];

    if (variantNotFoundState.productSubcategoryId) {
      departmentIdsToCheck.push(variantNotFoundState.productSubcategoryId);
    }
    if (variantNotFoundState.productCategoryId) {
      departmentIdsToCheck.push(variantNotFoundState.productCategoryId);
    }
    if (variantNotFoundState.productDepartmentId) {
      departmentIdsToCheck.push(variantNotFoundState.productDepartmentId);
    }

    let defaultsFound = false;

    for (const id of departmentIdsToCheck) {
      const res = await GetDepartmentDefaults({ department_id: id }, true);
      if (res.status === StatusCode.OK && res.data) {
        defaultsFound = true;
        break;
      }
    }

    if (!defaultsFound) {
      setVariantNotFoundState({ ...variantNotFoundState, noDefaultsWarning: true });
      return;
    }

    const params = {
      title: variantNotFoundState.productName,
      price: variantNotFoundState.productPrice,
      department_id: variantNotFoundState.productDepartmentId,
      category_id: variantNotFoundState.productCategoryId,
      subcategory_id: variantNotFoundState.productSubcategoryId,
      sku: variantNotFoundState.sku,
    };

    const postProductRes = await PostProduct(params, true);

    if (postProductRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating new product")));
      return;
    }

    dispatch(showSuccess(t("Successfully created product")));

    const createdSku = variantNotFoundState.sku;

    const updatedSkus = skuState.skus.map(sku => (sku.sku === createdSku ? { ...sku, searched: false } : sku));

    const source = axios.CancelToken.source();

    ReactDOM.unstable_batchedUpdates(() => {
      setVariantNotFoundState(prevState => ({
        ...prevState,
        modalOpen: false,
        sku: "",
        productName: "",
        productPrice: 0,
        productDepartmentId: null,
        productCategoryId: null,
        productSubcategoryId: null,
      }));
      setSkuState(prevState => ({ ...prevState, skus: updatedSkus }));
    });

    void findSkuProduct(
      productState.selectedProducts,
      updatedSkus,
      setProductState,
      setSkuState,
      dispatch,
      source.token,
    );
  }

  function handleBrowseProductsVisibility() {
    const opening = !productState.openModal;

    setProductState(prevState => ({
      ...prevState,
      openModal: opening,
      productsToAdd: [...prevState.selectedProducts],
      ...(opening
        ? {
          productSearchPending: true,
          productSearchResults: undefined,
          productSearchTotalCount: undefined,
          productQuantities: quantitiesFromProducts(prevState.selectedProducts),
        }
        : {}),
    }));

    if (opening) {
      setBrowseFilterState({ search: "", browseFilter: defaultBrowseFilter });
      void loadBrowseMetadata();
    } else {
      setBrowseFilterState({ search: "", browseFilter: defaultBrowseFilter });
    }
  }

  async function loadBrowseMetadata() {
    const showLoading = browseDepartments.length === 0;

    if (showLoading) {
      setBrowseMetadataLoading(true);
    }

    try {
      const [departmentRes, countRes] = await Promise.all([
        GetDepartments({ extended: true }, false),
        GetProduct(
          {
            get_count: true,
            limit: 1,
            filters: [{ label: "uncategorized", value: [1] }],
          },
          false,
        ),
      ]);

      if (departmentRes.status === StatusCode.OK) {
        setBrowseDepartments((departmentRes.data ?? []).filter(department => department.type === "department"));
      }

      if (countRes.status === StatusCode.OK && countRes.data && !Array.isArray(countRes.data)) {
        const countData = countRes.data as { count?: number };
        setUncategorizedCount(countData.count ?? 0);
      }
    } finally {
      if (showLoading) {
        setBrowseMetadataLoading(false);
      }
    }
  }

  function handleAddProductsBrowseSearchChange(value: string) {
    setBrowseFilterState(prevState => ({ ...prevState, search: value }));
    setProductState(prev => ({ ...prev, productSearchPending: true }));
  }

  function handleAddProductsBrowseFilterChange(browseFilter: IBrowseFilter) {
    setBrowseFilterState(prevState => ({ ...prevState, browseFilter }));
    setProductState(prev => ({ ...prev, productSearchPending: true }));
  }

  function getProductSearchContext(): IFolderProductSearchContext {
    return {
      search: browseFilterState.search,
      browseFilter: browseFilterState.browseFilter,
      variants: true,
    };
  }

  async function loadMoreProducts() {
    let shouldLoad = false;
    let offset = 0;

    setProductState(prev => {
      if (
        prev.productSearchLoadingMore ||
        prev.productSearchPending ||
        !prev.productSearchResults ||
        prev.productSearchTotalCount == null ||
        prev.productSearchResults.length >= prev.productSearchTotalCount
      ) {
        return prev;
      }

      shouldLoad = true;
      offset = prev.productSearchResults.length;
      return { ...prev, productSearchLoadingMore: true };
    });

    if (!shouldLoad) {
      return;
    }

    try {
      const { products } = await fetchFolderProductSearchPage(
        GetProduct,
        getProductSearchContext(),
        offset,
        false,
        undefined,
        () => dispatch(showError(t("Failed to get products"))),
      );
      setProductState(prev => ({
        ...prev,
        productSearchResults: appendUniqueProducts(prev.productSearchResults ?? [], products),
        productSearchLoadingMore: false,
      }));
    } catch {
      setProductState(prev => ({ ...prev, productSearchLoadingMore: false }));
    }
  }

  async function selectAllMatchingProducts() {
    const loaded = productState.productSearchResults ?? [];
    const totalCount = productState.productSearchTotalCount ?? loaded.length;
    const fullyAddedIds = new Set(
      loaded.filter(product => withSelectableVariants(product) == null).map(product => product.id),
    );
    const fullySelectedIds = new Set(
      productState.productsToAdd
        .filter(selected => {
          const source = loaded.find(product => product.id === selected.id) ?? selected;
          const available = withSelectableVariants(source);
          if (available == null) {
            return true;
          }

          const selectedVariantIds = new Set((selected.variants ?? []).map(variant => variant.id));
          return available.variants.every(variant => selectedVariantIds.has(variant.id));
        })
        .map(product => product.id),
    );
    const excludeIds = new Set<number>([...fullyAddedIds, ...fullySelectedIds]);
    const remaining = Math.max(0, ITEMS_SELECT_ALL_LIMIT - productState.productsToAdd.length);
    const needsRemoteFetch = remaining > 0 && loaded.length < totalCount;

    if (needsRemoteFetch) {
      setProductState(prev => ({ ...prev, productSearchSelectAllPending: true }));
    }

    try {
      const toAdd = await buildSelectAllProductBatch(
        GetProduct,
        getProductSearchContext(),
        loaded,
        totalCount,
        excludeIds,
        remaining,
        () => dispatch(showError(t("Failed to get products"))),
      );

      const selectable = toAdd
        .map(product => withSelectableVariants(product))
        .filter((product): product is IProduct => product != null);

      setProductState(prev => {
        if (selectable.length === 0) {
          return { ...prev, productSearchSelectAllPending: false };
        }

        return {
          ...prev,
          productsToAdd: mergeSelectedProducts(prev.productsToAdd, selectable),
          productQuantities: withDefaultQuantities(prev.productQuantities, selectable),
          productSearchSelectAllPending: false,
        };
      });
    } catch {
      setProductState(prev => ({ ...prev, productSearchSelectAllPending: false }));
    }
  }

  function mergeSelectedProducts(existing: IProduct[], incoming: IProduct[]) {
    const next = [...existing];

    incoming.forEach(product => {
      const index = next.findIndex(selected => selected.id === product.id);
      if (index === -1) {
        next.push(product);
        return;
      }

      const existingVariantIds = new Set((next[index].variants ?? []).map(variant => variant.id));
      const mergedVariants = [...(next[index].variants ?? [])];
      (product.variants ?? []).forEach(variant => {
        if (!existingVariantIds.has(variant.id)) {
          mergedVariants.push(variant);
          existingVariantIds.add(variant.id);
        }
      });
      next[index] = { ...next[index], variants: mergedVariants, variant_count: mergedVariants.length };
    });

    return next;
  }

  function withSelectableVariants(product: IProduct): IProduct | null {
    const variants = (product.variants ?? []).filter(variant => !excludedVariantIdSet.has(variant.id));
    if (variants.length === 0) {
      return null;
    }

    return {
      ...product,
      variants,
      variant_count: variants.length,
    };
  }

  function handleSelectProductToAdd(productOrProducts: IProduct | IProduct[]) {
    const products = Array.isArray(productOrProducts) ? productOrProducts : [productOrProducts];
    const selectable = products
      .map(product => withSelectableVariants(product))
      .filter((product): product is IProduct => product != null);

    if (selectable.length === 0) {
      return;
    }

    setProductState(prev => ({
      ...prev,
      productsToAdd: mergeSelectedProducts(prev.productsToAdd, selectable),
      productQuantities: withDefaultQuantities(prev.productQuantities, selectable),
    }));
  }

  function handleUnselectProductToAdd(productOrProducts: IProduct | IProduct[]) {
    const products = Array.isArray(productOrProducts) ? productOrProducts : [productOrProducts];

    setProductState(prev => {
      let next = [...prev.productsToAdd];

      products.forEach(incoming => {
        const index = next.findIndex(product => product.id === incoming.id);
        if (index === -1) {
          return;
        }

        const removeIds = new Set((incoming.variants ?? []).map(variant => variant.id));
        if (removeIds.size === 0) {
          next = next.filter(product => product.id !== incoming.id);
          return;
        }

        const remaining = (next[index].variants ?? []).filter(variant => !removeIds.has(variant.id));
        if (remaining.length === 0) {
          next = next.filter(product => product.id !== incoming.id);
        } else {
          next[index] = { ...next[index], variants: remaining, variant_count: remaining.length };
        }
      });

      const remainingIds = new Set(next.map(product => product.id));
      const productQuantities = { ...prev.productQuantities };
      Object.keys(productQuantities).forEach(id => {
        if (!remainingIds.has(Number(id))) {
          delete productQuantities[Number(id)];
        }
      });

      return { ...prev, productsToAdd: next, productQuantities };
    });
  }

  function handleProductQuantityChange(productId: number, quantity: number) {
    setProductState(prev => ({
      ...prev,
      productQuantities: { ...prev.productQuantities, [productId]: quantity },
    }));
  }

  function getProductSeedQuantity(product: IProduct): number {
    const variantWithQuantity = product.variants?.find(variant => variant.quantity != null);
    const quantity = Number(variantWithQuantity?.quantity);
    if (!Number.isFinite(quantity) || quantity < 0) {
      return 1;
    }
    return quantity;
  }

  function quantitiesFromProducts(products: IProduct[]): Record<number, number> {
    return products.reduce<Record<number, number>>((quantities, product) => {
      quantities[product.id] = getProductSeedQuantity(product);
      return quantities;
    }, {});
  }

  function resolveProductQuantity(productId: number, quantities: Record<number, number>): number {
    const quantity = Number(quantities[productId]);
    if (!Number.isFinite(quantity) || quantity < 0) {
      return 1;
    }
    return quantity;
  }

  function applyProductQuantities(products: IProduct[], quantities: Record<number, number>): IProduct[] {
    return products.map(product => {
      const quantity = resolveProductQuantity(product.id, quantities);
      return {
        ...product,
        variants: (product.variants ?? []).map(variant => ({
          ...variant,
          quantity,
        })),
      };
    });
  }

  function withDefaultQuantities(quantities: Record<number, number>, products: IProduct[]): Record<number, number> {
    const next = { ...quantities };
    products.forEach(product => {
      if (next[product.id] == null) {
        next[product.id] = getProductSeedQuantity(product);
      }
    });
    return next;
  }

  return (
    <Page
      title={pageTitle.title}
      subtitle={pageTitle.subtitle}
      primaryAction={page === TransferPage.InventoryConfirmation ? submitAction : continueAction}
      secondaryActions={
        page !== TransferPage.InventoryDetails
          ? [
            {
              content: `${t("Back to Step")} ${page}`,
              action: () => setPage(prev => --prev),
            },
          ]
          : []
      }
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to Inventory"),
          url: "/admin/settings/inventory",
        },
      ]}
    >
      {page === TransferPage.InventoryDetails && (
        <Card>
          <Card.Section>
            {detailState?.purchaseOrder ? (
              <>
                <div>
                  <p className="inventory-receiving-vendor-label">{t("Purchase Order")}</p>
                </div>
                <div className="inventory-receiving-vendor-name">
                  <div>{detailState?.purchaseOrder?.po_number} - {detailState?.purchaseOrder?.vendor_name}</div>
                  <button
                    onClick={() =>
                      setDetailState(prevState => ({
                        ...prevState,
                        purchaseOrder: null,
                        purchaseOrderQuery: "",
                      }))
                    }
                  >
                    <Icon icon={faTimes} />
                  </button>
                </div>
              </>
            ) : (
              <Select
                label={t("Purchase Order")}
                onChange={(_, purchaseOrder: IPurchaseOrder) => {
                  const foundVendor = detailState?.vendors?.find(vendor => vendor?.id === purchaseOrder?.vendor_id);
                  setDetailState(prevState => ({ ...prevState, purchaseOrder, vendor: foundVendor ? foundVendor : prevState?.vendor, po_number: purchaseOrder?.po_number ?? "" }));
                }
                }
                showSearch
                searchValue={detailState.purchaseOrderQuery}
                searching={detailState.searching}
                onSearch={(query: string) => setDetailState(prevState => ({ ...prevState, purchaseOrderQuery: query }))}
                showDropDownOnFocus
                placeholder={t("Search purchase orders...")}
              >
                {detailState.purchaseOrders?.map((purchaseOrder, index) => {
                  return (
                    <Option key={index} value={purchaseOrder?.id} extraValues={purchaseOrder}>
                      {purchaseOrder?.po_number} - {purchaseOrder?.vendor_name}
                      {/* <span className="font-medium text-gray-500 text-sm">{moment.utc(purchaseOrder?.finalized_at, "YYYY-MM-DD HH:mm:ss")?.local()?.format("LL")}</span> */}
                    </Option>
                  );
                })}
              </Select>
            )}
          </Card.Section>
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                {detailState?.vendor ? (
                  <>
                    <div>
                      <p className="inventory-receiving-vendor-label">{t("Vendor")}</p>
                    </div>
                    <div className="inventory-receiving-vendor-name">
                      <div>{detailState?.vendor?.title}</div>
                      <button
                        onClick={() =>
                          setDetailState(prevState => ({
                            ...prevState,
                            vendor: null,
                            vendorQuery: "",
                          }))
                        }
                      >
                        <Icon icon={faTimes} />
                      </button>
                    </div>
                  </>
                ) : (
                  <Select
                    label={t("Vendor")}
                    onChange={(value: number, vendor: TProductVendor) =>
                      setDetailState(prevState => ({ ...prevState, vendor }))
                    }
                    showSearch
                    searchValue={detailState.vendorQuery}
                    searching={detailState.searching}
                    onSearch={(query: string) => setDetailState(prevState => ({ ...prevState, vendorQuery: query }))}
                    showDropDownOnFocus
                    placeholder={t("Search vendors...")}
                  >
                    {detailState.vendors?.map((vendor, index) => {
                      return (
                        <Option key={index} value={vendor?.id} extraValues={vendor}>
                          {vendor?.title}
                        </Option>
                      );
                    })}
                  </Select>
                )}
                <Input
                  value={detailState.packingSlip}
                  label={t("Packing Slip")}
                  placeholder="012345"
                  onChange={e => setDetailState(prevState => ({ ...prevState, packingSlip: e.target.value }))}
                />

                <Input
                  value={detailState.po_number}
                  label={t("PO Number")}
                  placeholder="012345"
                  onChange={e => setDetailState(prevState => ({ ...prevState, po_number: e.target.value }))}
                />
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
        </Card>
      )}

      {page === TransferPage.InventorySelection && (
        <Card>
          <Card.Section>
            <div className="inventory-receiving-sticky-bar mb-4">
              <div className="inventory-receiving-sticky-bar-inner">
                <FormLayout>
                  <FormLayout.Group>
                    <Input
                      id="skuSearchId"
                      onKeyDown={handleEnterKeydown}
                      value={skuState.skuString}
                      placeholder={t("Scan Sku / Barcode")}
                      onChange={e => setSkuState(prevState => ({ ...prevState, skuString: e.target.value }))}
                      trailingButtons={[
                        <Button key={1} onClick={handleEnterClick} type="secondary">
                          {t("Enter")}
                        </Button>,
                        <Button
                          key={2}
                          type="primary"
                          size="medium"
                          icon={<FontAwesomeIcon className="ml-2" icon={["far", "magnifying-glass"]} />}
                          onClick={() => handleBrowseProductsVisibility()}
                        >
                          {t("Search")}
                        </Button>,
                      ]}
                    />
                  </FormLayout.Group>
                </FormLayout>

                {productState?.selectedProducts?.length > 0 && (
                  <div className="inventory-receiving-sticky-totals">
                    <p className="inventory-receiving-sticky-total">
                      <span className="inventory-receiving-sticky-total-label">{t("Total Cost")}:</span>{" "}
                      <span className="inventory-receiving-sticky-total-value">
                        {displayCurrency("cad", totals?.cost ?? 0)}
                      </span>
                    </p>
                    <p className="inventory-receiving-sticky-total">
                      <span className="inventory-receiving-sticky-total-label">{t("Quantity")}:</span>{" "}
                      <span className="inventory-receiving-sticky-total-value">{totals?.quantity ?? 0}</span>
                    </p>
                  </div>
                )}
              </div>
            </div>
            <DataTable
              columns={[
                ...(productState?.selectedProducts?.length > 0 ? [{ label: t("Title") }, { label: t("Vendor") }] : []),
                ...(productState?.selectedProducts?.length > 0
                  ? [
                    { label: t("Sku") },
                    { label: t("Barcode") },
                    { label: t("Total Cost") },
                    { label: t("Unit Cost") },
                  ]
                  : [{ label: t("Scan Sku / Barcode") }, { label: "" }]),
                {
                  label: t("Quantity"),
                  content: (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="headingTooltip">{`${t(
                          "Quantity can be manually adjusted on the next step",
                        )}`}</Tooltip>
                      }
                    >
                      <div>
                        <span className="inventory-receiving-quantity-label">{t("Quantity")} </span>
                        <FontAwesomeIcon size="sm" icon={faSquareInfo} />
                      </div>
                    </OverlayTrigger>
                  ),
                },
                { label: "" },
                { label: "" },
                { label: "" },
              ]}
              footerTotals={
                productState?.selectedProducts?.length > 0
                  ? { totalCost: totals?.cost ?? 0, quantity: totals?.quantity ?? 0 }
                  : undefined
              }
            >
              {skuState?.skus?.map((sku, index) => {
                return (
                  <tr key={index}>
                    {productState?.selectedProducts?.length > 0 && (
                      <>
                        <td></td>
                        <td></td>
                        <td>{sku?.sku}</td>
                      </>
                    )}
                    <td>{sku?.sku}</td>
                    {productState?.selectedProducts?.length > 0 && <td></td>}
                    <td></td>
                    <td>{sku?.quantity}</td>
                    <td className="text-right">
                      {!sku.searched ? (
                        <div style={{ display: "inline-flex" }}>
                          <Spin />
                        </div>
                      ) : (
                        <OverlayTrigger
                          placement="auto"
                          overlay={<Tooltip id="headingTooltip">{`${t("Product not found")}`}</Tooltip>}
                        >
                          <FontAwesomeIcon
                            className="inventory-receiving-error"
                            size="1x"
                            icon={faCircleXmark}
                            onClick={() =>
                              setVariantNotFoundState(prevState => ({ ...prevState, modalOpen: true, sku: sku.sku }))
                            }
                          />
                        </OverlayTrigger>
                      )}
                    </td>
                    <td className="text-right">
                      {sku.searched && (
                        <Button
                          type="text"
                          size="small"
                          onClick={() =>
                            setVariantNotFoundState(prevState => ({ ...prevState, modalOpen: true, sku: sku.sku }))
                          }
                        >
                          {t("Create product")}
                        </Button>
                      )}
                    </td>
                    <td className="text-right">
                      <FontAwesomeIcon
                        onClick={() => removeSku(index)}
                        icon={faTrashCanXmark}
                        size="1x"
                        className="cursor-pointer"
                      />
                    </td>
                  </tr>
                );
              })}

              {productState?.selectedProducts?.map((product, productIndex) => {
                return (
                  <React.Fragment key={productIndex}>
                    <tr
                      className="inventory-receiving-product-header cursor-pointer"
                      onClick={() => handleEditProduct(product.id)}
                    >
                      <td>{product?.preferred_title ? product?.preferred_title : product.title}</td>
                      <td>{product.vendor_title}</td>
                      <td>{product?.variant_count === 1 && product?.variants[0]?.sku}</td>
                      <td>{product?.variant_count === 1 && product?.variants[0]?.barcode}</td>
                      {product?.variant_count === 1 && (
                        <>
                          <td>
                            <LocaleCurrency
                              currency="cad"
                              amount={
                                product?.variants[0]?.cost
                                  ? product?.variants[0]?.cost * product?.variants[0]?.quantity
                                  : 0
                              }
                            />
                          </td>
                          <td>
                            <LocaleCurrency
                              currency="cad"
                              amount={product?.variants[0]?.cost ? product?.variants[0]?.cost : 0}
                            />
                          </td>
                        </>
                      )}
                      <td>
                        {product?.variant_count === 1 && product?.variants[0]?.quantity}
                        {product?.variant_count === 1 && detailState?.purchaseOrder?.items ? (
                          <p className="text-sm text-gray-500">
                            {t("PO")}: {detailState.purchaseOrder.items.find(item => item.variant_id === product.variants[0]?.id)?.quantity ?? "-"}
                          </p>
                        ) : null}
                      </td>
                      <td className="text-right">
                        {product?.variant_count === 1 && (
                          <OverlayTrigger
                            placement="auto"
                            overlay={
                              <Tooltip id="headingTooltip">{`${t(
                                "secure.facility.settings.inventory.new_receiving.039",
                              )}`}</Tooltip>
                            }
                          >
                            <FontAwesomeIcon className="inventory-receiving-success" size="1x" icon={faCircleCheck} />
                          </OverlayTrigger>
                        )}
                      </td>
                      <td className="text-right"></td>
                      <td className="text-right">
                        {product?.variant_count === 1 && (
                          <FontAwesomeIcon
                            onClick={e => {
                              e.stopPropagation();
                              removeVariant(productIndex, "selectedProducts");
                            }}
                            icon={faTrashCanXmark}
                            size="1x"
                            className="cursor-pointer"
                          />
                        )}
                      </td>
                      {product?.variant_count > 1 && (
                        <>
                          <td className="text-right"></td>
                          <td> </td>
                        </>
                      )}
                    </tr>
                    {product?.variant_count > 1 &&
                      product?.variants?.map((variant, variantIndex) => (
                        <tr key={variantIndex} className="cursor-pointer" onClick={() => handleEditProduct(product.id)}>
                          <td style={{ textIndent: "3rem" }}>{variant?.title}</td>
                          <td></td>
                          <td>{variant?.sku}</td>
                          <td>{variant?.barcode}</td>
                          <td>
                            <LocaleCurrency
                              currency="cad"
                              amount={variant?.cost ? variant?.cost * variant?.quantity : 0}
                            />
                          </td>
                          <td>
                            <LocaleCurrency currency="cad" amount={variant?.cost} />
                          </td>
                          <td>
                            {variant?.quantity}
                            {detailState?.purchaseOrder?.items ? (
                              <p className="text-sm text-gray-500">
                                {t("PO")}: {detailState.purchaseOrder.items.find(item => item.variant_id === variant?.id)?.quantity ?? "-"}
                              </p>
                            ) : null}
                          </td>
                          <td className="text-right">
                            {
                              <OverlayTrigger
                                placement="auto"
                                overlay={
                                  <Tooltip id="headingTooltip">{`${t(
                                    "secure.facility.settings.inventory.new_receiving.039",
                                  )}`}</Tooltip>
                                }
                              >
                                <FontAwesomeIcon
                                  className="inventory-receiving-success"
                                  size="1x"
                                  icon={faCircleCheck}
                                />
                              </OverlayTrigger>
                            }
                          </td>
                          <td className="text-right"></td>
                          <td className="text-right">
                            <FontAwesomeIcon
                              onClick={e => {
                                e.stopPropagation();
                                removeVariant(productIndex, "selectedProducts", variantIndex);
                              }}
                              icon={faTrashCanXmark}
                              size="1x"
                              className="cursor-pointer"
                            />
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })}
            </DataTable>
          </Card.Section>
        </Card>
      )}

      {page === TransferPage.InventoryConfirmation && (
        <>
          <Card>
            <Card.Section>
              <div className="inventory-receiving-sticky-bar mb-4">
                <div className="inventory-receiving-sticky-bar-inner">
                  <FormLayout>
                    <FormLayout.Group>
                      <Input
                        onKeyDown={handleEnterKeydown}
                        value={skuState.skuString}
                        placeholder={t("Scan Sku / Barcode")}
                        onChange={e => setSkuState(prevState => ({ ...prevState, skuString: e.target.value }))}
                        trailingButtons={[
                          <Button key={1} onClick={handleEnterClick} type="secondary">
                            {t("Enter")}
                          </Button>,
                          <Button
                            key={2}
                            type="primary"
                            size="medium"
                            icon={<FontAwesomeIcon className="ml-2" icon={["far", "magnifying-glass"]} />}
                            onClick={() => handleBrowseProductsVisibility()}
                          >
                            {t("Search")}
                          </Button>,
                        ]}
                      />
                    </FormLayout.Group>
                  </FormLayout>

                  {productState?.selectedProducts?.length > 0 && (
                    <div className="inventory-receiving-sticky-totals">
                      <p className="inventory-receiving-sticky-total">
                        <span className="inventory-receiving-sticky-total-label">{t("Total Cost")}:</span>{" "}
                        <span className="inventory-receiving-sticky-total-value">
                          {displayCurrency("cad", totals?.cost ?? 0)}
                        </span>
                      </p>
                      <p className="inventory-receiving-sticky-total">
                        <span className="inventory-receiving-sticky-total-label">{t("Quantity")}:</span>{" "}
                        <span className="inventory-receiving-sticky-total-value">{totals?.quantity ?? 0}</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <DataTable
                columns={[
                  { label: t("Title") },
                  { label: t("Vendor") },
                  { label: t("Sku") },
                  { label: t("Barcode") },
                  { label: t("Total Cost") },
                  { label: t("Unit Cost") },
                  { label: t("Received Quantity") },
                  ...(purchaseOrderItemExists ? [{ label: t("Ordered Quantity") }] : []),
                  { label: "" },
                ]}

                footerTotals={
                  productState?.selectedProducts?.length > 0
                    ? { totalCost: totals?.cost ?? 0, quantity: totals?.quantity ?? 0 }
                    : undefined
                }
                loading={!productState.finalSelectedProducts}
              >
                {productState?.finalSelectedProducts?.map((product, productIndex) => {
                  const purchaseOrderItem = purchaseOrderItemExists && product?.variant_count === 1 ? detailState?.purchaseOrder?.items?.find(item => item?.product_id === product?.id && item?.variant_id === product?.variants[0]?.id) : null;
                  const orderedQuantity = purchaseOrderItem ? Number(purchaseOrderItem.quantity) : null;
                  const previouslyReceived = purchaseOrderItem ? Number(purchaseOrderItem.quantity_received ?? 0) : 0;
                  const openQuantity = orderedQuantity != null ? orderedQuantity - previouslyReceived : null;
                  const receivingDifference = openQuantity != null ? Number(product?.variants[0]?.quantity) - openQuantity : 0;
                  return (
                    <React.Fragment key={productIndex}>
                      <tr className="inventory-receiving-product-header">
                        <td>{product?.preferred_title ? product?.preferred_title : product.title}</td>
                        <td>{product.vendor_title}</td>
                        <td>{product?.variant_count === 1 && product?.variants[0]?.sku}</td>
                        <td>{product?.variant_count === 1 && product?.variants[0]?.barcode}</td>
                        {product?.variant_count === 1 && (
                          <>
                            <td>
                              <LocaleCurrency
                                currency="cad"
                                amount={
                                  product?.variants[0]?.cost
                                    ? product?.variants[0]?.cost * product?.variants[0]?.quantity
                                    : 0
                                }
                              />
                            </td>
                            <td>
                              <Input
                                type="number"
                                labelHidden
                                onChange={e => handleInputChange(e, "finalSelectedProducts", productIndex, 0, "cost")}
                                value={product?.variants[0]?.cost}
                                placeholder={t("Unit Cost")}
                              />
                            </td>
                          </>
                        )}
                        <td style={{ width: "15%" }}>
                          {product?.variant_count === 1 && (
                            <Input
                              type="number"
                              labelHidden
                              onChange={e => handleInputChange(e, "finalSelectedProducts", productIndex, 0)}
                              value={product?.variants[0]?.quantity ?? ""}
                              placeholder={t("Quantity")}
                            />
                          )}
                        </td>
                        {purchaseOrderItemExists && product?.variant_count === 1 ? (
                          <td>
                            {orderedQuantity ?? "-"}
                            {previouslyReceived > 0 ? <p className="text-sm text-gray-500">{t("{{count}} already received", { count: previouslyReceived })}</p> : null}
                            {openQuantity != null && receivingDifference !== 0 ? <span style={{ color: "red" }}>{`(${receivingDifference > 0 ? "+" : ""}${receivingDifference})`}</span> : null}
                          </td>
                        ) : purchaseOrderItemExists ? <td></td> : null}
                        <td>
                          {product?.variant_count === 1 && (
                            <FontAwesomeIcon
                              onClick={() => removeVariant(productIndex, "finalSelectedProducts")}
                              icon={faTrashCanXmark}
                              size="1x"
                              className="cursor-pointer"
                            />
                          )}
                        </td>
                        {product?.variant_count > 1 && (
                          <>
                            <td className="text-right"></td>
                            <td></td>
                          </>
                        )}
                      </tr>
                      {product?.variant_count > 1 &&
                        product?.variants?.map((variant, variantIndex) => {
                          const purchaseOrderItem = detailState?.purchaseOrder?.items ? detailState?.purchaseOrder?.items?.find(item => item?.variant_id === variant?.id) : null;
                          const orderedQuantity = purchaseOrderItem ? Number(purchaseOrderItem.quantity) : null;
                          const previouslyReceived = purchaseOrderItem ? Number(purchaseOrderItem.quantity_received ?? 0) : 0;
                          const openQuantity = orderedQuantity != null ? orderedQuantity - previouslyReceived : null;
                          const receivingDifference = openQuantity != null ? Number(variant?.quantity) - openQuantity : 0;
                          return (
                            <tr key={variantIndex}>
                              <td style={{ textIndent: "3rem" }}>{variant?.title}</td>
                              <td></td>
                              <td>{variant?.sku}</td>
                              <td>{variant?.barcode}</td>
                              <td>
                                <LocaleCurrency
                                  currency="cad"
                                  amount={variant?.cost ? variant?.cost * variant?.quantity : 0}
                                />
                              </td>
                              <td>
                                <Input
                                  type="number"
                                  labelHidden
                                  onChange={e =>
                                    handleInputChange(e, "finalSelectedProducts", productIndex, variantIndex, "cost")
                                  }
                                  value={variant?.cost}
                                  placeholder={t("Unit Cost")}
                                />
                              </td>
                              <td style={{ width: "15%" }}>
                                <Input
                                  type="number"
                                  labelHidden
                                  onChange={e =>
                                    handleInputChange(e, "finalSelectedProducts", productIndex, variantIndex)
                                  }
                                  value={variant?.quantity ?? ""}
                                  placeholder={t("Quantity")}
                                />
                              </td>
                              {purchaseOrderItemExists ? (
                                <td>
                                  {orderedQuantity ?? "-"}
                                  {previouslyReceived > 0 ? <p className="text-sm text-gray-500">{t("{{count}} already received", { count: previouslyReceived })}</p> : null}
                                  {openQuantity != null && receivingDifference !== 0 ? <span style={{ color: "red" }}>{`(${receivingDifference > 0 ? "+" : ""}${receivingDifference})`}</span> : null}
                                </td>
                              ) : null}

                              <td>
                                <FontAwesomeIcon
                                  onClick={() => removeVariant(productIndex, "finalSelectedProducts", variantIndex)}
                                  icon={faTrashCanXmark}
                                  size="1x"
                                  className="cursor-pointer"
                                />
                              </td>
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </DataTable>
            </Card.Section>
            {productState?.skusNotFound?.length > 0 && (
              <Card.Section title={t("Variant Skus / Barcodes Not Found")}>
                <DataTable columns={[{ label: t("Scan Sku / Barcode") }, { label: t("Quantity") }]}>
                  {productState?.skusNotFound?.map((sku, index) => {
                    return (
                      <tr key={index}>
                        <td>{sku.sku}</td>
                        <td>{sku.quantity}</td>
                      </tr>
                    );
                  })}
                </DataTable>
              </Card.Section>
            )}
          </Card>
        </>
      )}

      <AddFolderProductsModal
        open={productState.openModal}
        onClose={handleBrowseProductsVisibility}
        onAdd={handleProductSelect}
        search={browseFilterState.search}
        onSearchChange={handleAddProductsBrowseSearchChange}
        browseFilter={browseFilterState.browseFilter}
        onBrowseFilterChange={handleAddProductsBrowseFilterChange}
        departments={browseDepartments}
        departmentsLoading={browseMetadataLoading}
        uncategorizedCount={uncategorizedCount}
        productsToAdd={productState.productsToAdd}
        availableSlotCount={ITEMS_SELECT_ALL_LIMIT}
        totalEmptySlotCount={ITEMS_SELECT_ALL_LIMIT}
        folderProducts={[]}
        productSearchResults={productState.productSearchResults}
        productSearchTotalCount={productState.productSearchTotalCount}
        productSearchPending={productState.productSearchPending}
        productSearchLoadingMore={productState.productSearchLoadingMore}
        productSearchSelectAllPending={productState.productSearchSelectAllPending}
        onLoadMoreProducts={loadMoreProducts}
        onSelectAllMatchingProducts={selectAllMatchingProducts}
        onSelectProduct={handleSelectProductToAdd}
        onUnselectProduct={handleUnselectProductToAdd}
        hideSlots
        enableVariantSelection
        excludedVariantIds={excludedVariantIds}
        title={t("Browse products")}
        supportText={t("Add items to this purchase order")}
        onCreateProduct={() => {
          void handleBrowseProductsVisibility();
          history.push("/admin/product/new");
        }}
        onGoToProducts={() => {
          void handleBrowseProductsVisibility();
          history.push("/admin/product");
        }}
        browseOnOpen
        showQuantity
        productQuantities={productState.productQuantities}
        onQuantityChange={handleProductQuantityChange}
      />




      <Portal isMounted={submitPopupState}>
        <Popup
          open={submitPopupState}
          onOk={() => handleCreateNewReceiving(false)}
          onCancel={() => setSubmitPopupState(false)}
          okText={detailState?.purchaseOrder && purchaseOrderShortfalls.length > 0 ? t("Receive this shipment") : t("Confirm")}
          type={purchaseOrderItemDiscrepencies?.length > 0 ? "warning" : "info"}
          title={detailState?.purchaseOrder && purchaseOrderShortfalls.length > 0 ? t("Receive part of this purchase order?") : t("Submit New Receiving?")}
          noFixedWidth={purchaseOrderItemDiscrepencies?.length > 0 ? true : false}
          description={
            detailState?.purchaseOrder && purchaseOrderShortfalls.length > 0
              ? t("Only the quantities entered will be received. Anything still open stays on the purchase order so you can receive it when it arrives.")
              : t("Are you sure you want to submit new receiving? Please verify details before submission")
          }
        >
          {detailState?.purchaseOrder && purchaseOrderItemDiscrepencies?.length > 0 ?
            <>
              <br />
              <p className="text-semibold mb-1">{t("Purchase order quantities")}</p>
              <DataTable columns={[{ label: t("Product") }, { label: t("Receiving") }, { label: t("PO open") }, { label: t("Difference") }]}>
                {purchaseOrderItemDiscrepencies?.map((item, index) => {
                  const difference = Number(item?.current_quantity) - Number(item?.expected_quantity);
                  return (
                    <tr key={index}>
                      <td>
                        <p className="text-semibold">{item?.product_title}</p>
                        {item?.product_title !== item?.variant_title ? <p className="text-sm text-gray-500">{item?.variant_title}</p> : null}
                        <p className="text-sm text-gray-500">{t("PO quantity")}: {item.ordered_quantity}</p>
                      </td>
                      <td>{item?.current_quantity}</td>
                      <td>{item?.expected_quantity}</td>
                      <td style={{ color: "red" }}>{difference > 0 ? `+${difference}` : difference}</td>
                    </tr>
                  );
                })}
              </DataTable>
              {purchaseOrderShortfalls.length > 0 ? (
                <div className="mt-4">
                  <Button type="secondary" onClick={() => handleCreateNewReceiving(true)}>
                    {t("Receive and create credit memo")}
                  </Button>
                </div>
              ) : null}
            </>
            : null}
        </Popup>
      </Portal>

      <Portal isMounted={variantNotFoundState.modalOpen}>
        <Sheet
          title={t("Creating product for SKU: ") + variantNotFoundState?.sku}
          open={variantNotFoundState.modalOpen}
          onCancel={() =>
            setVariantNotFoundState(prevState => ({
              ...prevState,
              modalOpen: false,
              sku: "",
              productName: "",
              productPrice: 0,
              productDepartmentId: null,
              productCategoryId: null,
              productSubcategoryId: null,
            }))
          }
          cancelText={t("Cancel")}
          okText={t("Create")}
          closable
          onOk={() => handleCreateNewProduct()}
          size="medium"
          okDisabled={
            !variantNotFoundState.productName ||
            !variantNotFoundState.productPrice ||
            !variantNotFoundState.productDepartmentId
          }
        >
          <FormLayout>
            <FormLayout.Group>
              <Input
                label={t("Product Title")}
                value={variantNotFoundState.productName}
                onChange={e => setVariantNotFoundState({ ...variantNotFoundState, productName: e.target.value })}
              />

              <Input
                label={t("Product Price")}
                value={variantNotFoundState.productPrice}
                type="number"
                prefix="$"
                onChange={e =>
                  setVariantNotFoundState({ ...variantNotFoundState, productPrice: Number(e.target.value) })
                }
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <SelectNew
                id="department_select"
                label={t("secure.facility.product.product_new.037")}
                onChange={(value: number) => {
                  setVariantNotFoundState(prev => {
                    if (prev.productDepartmentId === value) {
                      return prev;
                    }
                    return {
                      ...prev,
                      productDepartmentId: value,
                      productCategoryId: null,
                      productSubcategoryId: null,
                    };
                  });
                }}
                options={departments
                  .filter(value => value.type === "department")
                  .map((department: IDepartments) => ({
                    id: department.id,
                    label: department.title,
                  }))}
                value={variantNotFoundState.productDepartmentId}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <SelectNew
                id="category_select"
                label={t("secure.facility.product.product_new.038")}
                onChange={(value: number) => {
                  setVariantNotFoundState(prev => {
                    if (prev.productCategoryId === value) {
                      return prev;
                    }
                    return {
                      ...prev,
                      productCategoryId: value,
                      productSubcategoryId: null,
                    };
                  });
                }}
                options={departments
                  .filter(
                    value =>
                      value.type === "category" && value.parent_id == Number(variantNotFoundState.productDepartmentId),
                  )
                  .map((category: ICategories) => ({
                    id: category.id,
                    label: category.title,
                  }))}
                value={variantNotFoundState.productCategoryId}
              />

              <SelectNew
                id="subcategory_select"
                label={t("secure.facility.product.product_new.039")}
                onChange={(value: number) => {
                  setVariantNotFoundState(prev => {
                    if (prev.productSubcategoryId === value) {
                      return prev;
                    }
                    return {
                      ...prev,
                      productSubcategoryId: value,
                    };
                  });
                }}
                options={departments
                  .filter(
                    value =>
                      value.type === "subcategory" && value.parent_id == Number(variantNotFoundState.productCategoryId),
                  )
                  .map((subcategory: ISubcategories) => ({
                    id: subcategory.id,
                    label: subcategory.title,
                  }))}
                value={variantNotFoundState.productSubcategoryId}
              />
            </FormLayout.Group>
          </FormLayout>
        </Sheet>
      </Portal>

      <Portal isMounted={variantNotFoundState.noDefaultsWarning}>
        <Popup
          open={variantNotFoundState.noDefaultsWarning}
          type="warning"
          title={t("No defaults found")}
          cancelText={t("Close")}
          description=""
          okText={t("Go")}
          onOk={() => {
            window.open("/admin/settings/departments", "_blank");
            setVariantNotFoundState({ ...variantNotFoundState, noDefaultsWarning: false });
          }}
          onCancel={() => setVariantNotFoundState({ ...variantNotFoundState, noDefaultsWarning: false })}
          closable
          stacked
        >
          <p>
            {t("You can configure defaults on the")}{" "}
            <a href="/admin/settings/departments" target="_blank" rel="noopener noreferrer"
              className="text-semibold" >
              {t("Departments settings page")}
            </a>
            .
          </p>
        </Popup>
      </Portal>
    </Page>
  );
}
